import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Alert, Button, Container, Paper, Typography } from '@mui/material'
import Wrapper from '../components/Wrapper'
import GuidedDiveRequirements from '../components/GuidedDiveRequirements'
import { useSessionStore } from '../state'
import { requalifyGuidedDive } from '../services/accreditationService'
import routes from './routes'
import type Database from '../types/Database'

const RequalifyGuide = () => {
  const navigate = useNavigate()
  const userId = useSessionStore(s => s.user?.uid)
  const profile = useSessionStore(s => s.profile)
  const setProfile = useSessionStore(s => s.setProfile)

  const docsRef = useRef<Database.GuidedDiveSubmissionDocs | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleDocsReady = useCallback(
    (docs: Database.GuidedDiveSubmissionDocs | null) => {
      docsRef.current = docs
    },
    []
  )

  const handleSubmit = async () => {
    if (!userId) return
    if (!docsRef.current) {
      setSubmitError(
        'Please complete all professional requirements before submitting.'
      )
      return
    }

    setSubmitError(null)
    setIsSubmitting(true)

    try {
      await requalifyGuidedDive(userId, docsRef.current)
      setSuccess(true)

      if (profile) {
        setProfile({ ...profile, guidedDiveFlag: true })
      }

      setTimeout(() => navigate(routes.home), 2000)
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Submission failed. Please try again.'
      setSubmitError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <Wrapper>
        <Container maxWidth='sm'>
          <Paper sx={{ p: 3, mt: 2 }}>
            <Alert severity='success'>
              Your professional documents have been updated. You are now
              eligible to offer guided dives again. Redirecting...
            </Alert>
          </Paper>
        </Container>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Container maxWidth='sm'>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant='h5' gutterBottom>
            Re-qualify for Guided Dives
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Your professional status or insurance has expired. Upload new
            documents with valid expiry dates to regain eligibility to lead
            guided dives.
          </Typography>

          {submitError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {userId && (
            <GuidedDiveRequirements
              userId={userId}
              provider={profile?.accreditation?.type ?? ''}
              disabled={isSubmitting}
              onDocsReady={handleDocsReady}
            />
          )}

          <Button
            variant='contained'
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Re-qualify'}
          </Button>
        </Paper>
      </Container>
    </Wrapper>
  )
}

export default RequalifyGuide
